import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { AppConfig } from '@config/app.config';
import { LANGUAGE_CONFIG } from '@config/language.config';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class ValidationsService {
  static getValidatorErrorMessage(validatorName: string, validatorValue?: any) {
    const lang = LANGUAGE_CONFIG;
    const config = {
      required: lang.message.required,
      invalidBetweenToday: lang.transfers.betweenToday,
      invalidBetweenYesterday: lang.transfers.betweenYesterday,
      invalidCell: lang.transfers.invalidCell,
      invalidMinimumDate: lang.transfers.afterMinimum,
      invalidMinorToday: lang.transfers.minorToday,
      invalidNumber: lang.message.invalidNumber,
      invalidPercent: lang.message.invalidPercent,
      invalidRangeCommissions: lang.commissions.greaterStartEnd,
      invalidSpaces: lang.message.invalidSpaces,
      invalidText: lang.message.invalidText
    };

    return config[validatorName];
  }

  static isInvalidNumberValidator(control: AbstractControl) {
    const expresion = new RegExp('^([0-9])*$');

    if (expresion.test(control.value)) {
      return null;
    }

    return { invalidNumber: true };
  }

  static isInvalidTextValidator(control: AbstractControl) {
    const expresion = new RegExp('^([a-zA-ZñÑáéíóúÁÉÍÓÚ])+$');

    if (control.value && expresion.test(control.value)) {
      return null;
    }

    return { invalidText: true };
  }

  static isFixValueValidator(control: AbstractControl) {
    const regex = /^(([0-9]){1,7})(\.([0-9]{1,2}))?$/gm;
    const test = regex.exec(control.value);

    if (test === null) {
      return { invalidPercent: true };
    }

    return null;
  }

  static isVariableValueValidator(control: AbstractControl) {
    const regex = /^(([0-9]){1,3})(\.([0-9]{1,3}))?$/gm;
    const test = regex.exec(control.value);

    if (test === null) {
      return { invalidPercent: true };
    }

    return null;
  }

  static isInvalidCellValidator(control: AbstractControl) {
    const expresion = new RegExp('^3');

    if (expresion.test(control.value) && control.value.length === 10) {
      return null;
    }

    return { invalidCell: true };
  }

  static notHaveSpaces(control: AbstractControl) {
    if (control.value) {
      if (control.value.indexOf(' ') >= 0) {
        return { invalidSpaces: true };
      }
    }

    return null;
  }

  static isMinorThanMinimumDate(control: AbstractControl) {
    const appConfig = new AppConfig();
    const date = moment()
      .subtract(appConfig.MIN_DATE.value, appConfig.MIN_DATE.time)
      .startOf('day');
    if (control.value && control.value.isBefore(date)) {
      return { invalidMinimumDate: true };
    }

    return null;
  }

  static isMinorThanToday(control: AbstractControl) {
    const today = moment();

    if (control.value && !control.value.isSameOrBefore(today)) {
      return { invalidMinorToday: true };
    }

    return null;
  }

  static isDateBetweenNinetyDaysFromYesterday(control: AbstractControl) {
    const daysBefore = moment()
        .subtract(91, 'days')
        .startOf('day'),
      yesterday = moment()
        .subtract(1, 'days')
        .endOf('day');

    if (control.value) {
      const newControl = moment(new Date(control.value));
      if (newControl.isBefore(daysBefore) || newControl.isAfter(yesterday)) {
        return { invalidBetweenYesterday: true };
      }
    }

    return null;
  }

  static isDateBetweenNinetyDaysFromToday(control: AbstractControl) {
    const daysBefore = moment()
        .subtract(90, 'days')
        .startOf('day'),
      today = moment().endOf('day');

    if (control.value) {
      const newControl = moment(new Date(control.value));
      if (newControl.isBefore(daysBefore) || newControl.isAfter(today)) {
        return { invalidBetweenToday: true };
      }
    }

    return null;
  }

  static validateRangeCommissions(formGroup: FormGroup) {
    const start = formGroup.get('startValue');
    const end = formGroup.get('endValue');

    if (ValidationsService.isRangeMinorThan(start, end)) {
      return null;
    }

    return { invalidRangeCommissions: true };
  }

  static validateRangeAmlLimitValue(formGroup: FormGroup) {
    const start = formGroup.get('monthlyValue');
    const end = formGroup.get('annualValue');

    if (ValidationsService.isRangeMinorOrEqualThan(start, end)) {
      return null;
    }

    return { invalidRangeAmlLimitValue: true };
  }

  static validateRangeAmlLimitCount(formGroup: FormGroup) {
    const start = formGroup.get('monthlyCount');
    const end = formGroup.get('annualCount');

    if (ValidationsService.isRangeMinorOrEqualThan(start, end)) {
      return null;
    }

    return { invalidRangeAmlLimitCount: true };
  }

  static validateRangeDateValue(formGroup: FormGroup) {
    const start = formGroup.get('dateStart');
    const end = formGroup.get('dateEnd');

    if (start.value === null || end.value === null) {
      return { invalidRangeDateFormatValue: true };
    } else if (
      start.value &&
      end.value &&
      !start.value.isSameOrBefore(end.value)
    ) {
      return { invalidRangeDateValue: true };
    }

    return null;
  }

  static isRangeMinorThan(start: any, end: any) {
    const startValue = parseInt(start.value, 10);
    const endValue = parseInt(end.value, 10);

    if (
      this.isInvalidNumberValidator(start) === null &&
      this.isInvalidNumberValidator(end) === null &&
      start.value !== '' &&
      end.value !== '' &&
      startValue >= endValue
    ) {
      return false;
    }

    return true;
  }

  static isRangeMinorOrEqualThan(start: any, end: any) {
    const startValue = parseInt(start.value, 10);
    const endValue = parseInt(end.value, 10);

    if (
      this.isInvalidNumberValidator(start) === null &&
      this.isInvalidNumberValidator(end) === null &&
      start.value !== '' &&
      end.value !== '' &&
      startValue > endValue
    ) {
      return false;
    }

    return true;
  }
}
